import React, { useCallback, useMemo, useState } from 'react';
import { FileCheck, Pencil, LogIn } from 'lucide-react';
import AccordionSection from '../UI/AccordionSection';
import { CountBadge } from '../UI/CountBadge';
import PillToggle from '../UI/PillToggle';
import LoadingSpinner from '../UI/LoadingSpinner';
import { staffAPI } from '../../services/api';
import { formatDateTimeCompact } from '../../utils/dateUtils';
import {
  filterProfileAuditLog,
  formatProfileAuditValue,
  getProfileAuditActionLabel,
  getProfileAuditFieldLabel,
  parseProfileAuditEntry,
  PROFILE_AUDIT_ACTION_COLORS,
} from '../../utils/profileAuditLogUtils';

const ProfileAuditChangeCell = ({ fieldKey, fromValue, toValue }) => {
  const from = formatProfileAuditValue(fieldKey, fromValue);
  const to = formatProfileAuditValue(fieldKey, toValue);

  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm min-w-0">
      <span className={from.isEmpty ? 'text-gray-400 italic' : 'text-gray-500 line-through'}>
        {from.display}
      </span>
      <span className="text-gray-400 mx-0.5" aria-hidden="true">→</span>
      <span className={to.isEmpty ? 'text-gray-400 italic' : 'font-medium text-gray-900'}>
        {to.display}
      </span>
    </div>
  );
};

const ActionTypePill = ({ actionType }) => {
  const actionColor = PROFILE_AUDIT_ACTION_COLORS[actionType] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${actionColor}`}>
      {getProfileAuditActionLabel(actionType)}
    </span>
  );
};

/**
 * Actor cell — HR scans people by name; staff_id stays as secondary audit context.
 */
const ActorCell = ({ actorName, actorId, compact = false }) => {
  if (!actorName && !actorId) {
    return <span className={`text-gray-400 ${compact ? 'text-[11px]' : 'text-sm'}`}>—</span>;
  }

  const primary = actorName || actorId;
  const showSecondaryId = Boolean(actorName && actorId && actorName !== actorId);

  if (compact) {
    return (
      <span className="font-mono flex-shrink-0 truncate max-w-[40%] text-[11px] text-gray-500" title={showSecondaryId ? `${actorName} · ${actorId}` : primary}>
        {primary}
      </span>
    );
  }

  return (
    <div className="min-w-0">
      <div className="text-sm text-gray-900 truncate" title={showSecondaryId ? actorId : undefined}>
        {primary}
      </div>
      {showSecondaryId && (
        <div className="text-[11px] font-mono text-gray-400 truncate">{actorId}</div>
      )}
    </div>
  );
};

/** Desktop table row */
const ProfileAuditRow = ({ entry }) => {
  const parsed = parseProfileAuditEntry(entry);
  const actionType = entry.audit_action_type || 'UPDATE';

  return (
    <tr className="table-row">
      <td className="table-cell text-right align-top tabular-nums">
        <div className="text-sm text-gray-900">{formatDateTimeCompact(entry.audit_timestamp)}</div>
      </td>
      <td className="table-cell align-top">
        <ActionTypePill actionType={actionType} />
      </td>
      <td className="table-cell align-top min-w-0">
        {parsed.kind === 'field_change' ? (
          <span className="text-sm font-medium text-gray-900">
            {getProfileAuditFieldLabel(parsed.fieldKey)}
          </span>
        ) : (
          <span className="text-sm text-gray-600">
            {parsed.kind === 'login' || parsed.kind === 'logout' ? 'Portal access' : 'System event'}
          </span>
        )}
      </td>
      <td className="table-cell align-top min-w-0">
        {parsed.kind === 'field_change' ? (
          <ProfileAuditChangeCell
            fieldKey={parsed.fieldKey}
            fromValue={parsed.fromValue}
            toValue={parsed.toValue}
          />
        ) : (
          <span className="text-sm text-gray-600">{parsed.summary}</span>
        )}
      </td>
      <td className="table-cell align-top min-w-0">
        {parsed.kind === 'field_change' ? (
          <ActorCell actorName={parsed.actorName} actorId={parsed.actorId} />
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )}
      </td>
    </tr>
  );
};

/**
 * Mobile card — read-only audit entry (no detail modal).
 * HR scans: what changed → from/to → when · who.
 */
const ProfileAuditCard = ({ entry }) => {
  const parsed = parseProfileAuditEntry(entry);
  const actionType = entry.audit_action_type || 'UPDATE';
  const primaryLabel = parsed.kind === 'field_change'
    ? getProfileAuditFieldLabel(parsed.fieldKey)
    : (parsed.kind === 'login' || parsed.kind === 'logout' ? 'Portal access' : 'System event');

  return (
    <div className="bg-gray-50 rounded-xl p-3.5 border border-gray-100 min-w-0">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900 min-w-0 truncate" title={primaryLabel}>
          {primaryLabel}
        </p>
        <ActionTypePill actionType={actionType} />
      </div>

      <div className="mt-2 min-w-0">
        {parsed.kind === 'field_change' ? (
          <ProfileAuditChangeCell
            fieldKey={parsed.fieldKey}
            fromValue={parsed.fromValue}
            toValue={parsed.toValue}
          />
        ) : (
          <p className="text-sm text-gray-600 break-words">{parsed.summary}</p>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-gray-500">
        <span className="tabular-nums truncate">
          {formatDateTimeCompact(entry.audit_timestamp)}
        </span>
        {parsed.kind === 'field_change' ? (
          <ActorCell actorName={parsed.actorName} actorId={parsed.actorId} compact />
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </div>
    </div>
  );
};

/**
 * Collapsible profile direct-edit audit trail for the employee Employment tab.
 * Defaults to field edits only — login noise is hidden until HR opts in.
 */
export default function ProfileChangeLogSection({ staffId }) {
  const [expanded, setExpanded] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [filterMode, setFilterMode] = useState('edits');

  const loadEntries = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    try {
      const res = await staffAPI.getStaffAuditLog(staffId, { page_size: 100 });
      setEntries(res.data?.results || res.data || []);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [staffId]);

  const handleToggle = () => {
    setExpanded((open) => {
      if (!open && !loaded) loadEntries();
      return !open;
    });
  };

  const fieldEditEntries = useMemo(
    () => filterProfileAuditLog(entries, 'edits'),
    [entries],
  );
  const visibleEntries = useMemo(
    () => filterProfileAuditLog(entries, filterMode),
    [entries, filterMode],
  );

  const trailingCount = loaded ? fieldEditEntries.length : null;

  return (
    <AccordionSection
      expanded={expanded}
      onToggle={handleToggle}
      theme="admin"
      contentVariant="flush"
      icon={<FileCheck className="w-4 h-4 text-red-600" />}
      iconBg="bg-red-50"
      title="Profile Change Log"
      trailing={trailingCount != null && trailingCount > 0 ? (
        <CountBadge count={trailingCount} theme="admin" />
      ) : null}
    >
      <div className="border-t border-gray-200 px-4 py-3 bg-gray-50/60">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <p className="text-sm text-gray-600 max-w-2xl min-w-0">
            Direct edits and probation amendments — name, contact, pass details, employment dates.
            Org events and join/probation milestones are under Career Timeline & History on this Employment tab.
          </p>
          {loaded && entries.length > 0 && (
            <PillToggle
              variant="text"
              theme="admin"
              layout="sectionHeader"
              value={filterMode}
              onChange={setFilterMode}
              ariaLabel="Profile change log filter"
              options={[
                { id: 'edits', label: 'Field edits', icon: Pencil },
                { id: 'all', label: 'All activity', icon: LogIn },
              ]}
            />
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-3 py-12 border-t border-gray-200">
          <LoadingSpinner size="md" />
          <span className="text-sm text-gray-500">Loading change log…</span>
        </div>
      ) : !loaded ? null : visibleEntries.length === 0 ? (
        <div className="text-center py-12 border-t border-gray-200">
          <FileCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {filterMode === 'edits' ? 'No field edits recorded' : 'No activity recorded'}
          </h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            {filterMode === 'edits'
              ? 'When HR edits this profile via Edit Profile, each corrected field appears here with who changed it and when.'
              : 'Portal logins and profile edits will appear here once activity is recorded.'}
          </p>
          {filterMode === 'edits' && fieldEditEntries.length === 0 && entries.length > 0 && (
            <button
              type="button"
              onClick={() => setFilterMode('all')}
              className="mt-4 text-sm font-medium text-red-600 hover:text-red-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded"
            >
              Show {entries.length} portal login{entries.length === 1 ? '' : 's'}
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile / narrow — stacked cards (DS responsive data tables) */}
          <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 border-t border-gray-200">
            {visibleEntries.map((entry) => (
              <ProfileAuditCard key={entry.id} entry={entry} />
            ))}
          </div>

          {/* Desktop — fixed-track table; overflow-x only as a narrow-window safety net */}
          <div className="hidden md:block overflow-x-auto border-t border-gray-200">
            <table className="w-full table-fixed divide-y divide-gray-200">
              <colgroup>
                <col className="w-[18%]" />
                <col className="w-[12%]" />
                <col className="w-[18%]" />
                <col className="w-[36%]" />
                <col className="w-[16%]" />
              </colgroup>
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell text-right">When</th>
                  <th className="table-header-cell">Type</th>
                  <th className="table-header-cell">Field</th>
                  <th className="table-header-cell">Change</th>
                  <th className="table-header-cell">By</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {visibleEntries.map((entry) => (
                  <ProfileAuditRow key={entry.id} entry={entry} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AccordionSection>
  );
}
